import { isAxiosError } from "axios";
import { replacePricebookItems, updatePriceBook } from "../../../../services";
import { pushGlobalSuccess } from "../../../../success/globalSuccess";
import { PricebookItemInput } from "../../../../types/price-book.type";

/** Status the API refuses to rewrite a reserved pricebook's lines with. */
const HTTP_CONFLICT = 409;

const PRICES_SAVED_NOTICE = "บันทึกราคาแล้ว";

// The two writes run in sequence, so a failure has to say what already landed.
const BOOK_DISCOUNT_FAILED =
  "บันทึกส่วนลดสมุดไม่สำเร็จ ยังไม่มีการเปลี่ยนแปลงใดถูกบันทึก";
const ITEMS_FAILED =
  "บันทึกรายการสินค้าไม่สำเร็จ ยังไม่มีการเปลี่ยนแปลงใดถูกบันทึก";
const ITEMS_FAILED_AFTER_BOOK_DISCOUNT =
  "บันทึกส่วนลดสมุดแล้ว แต่บันทึกรายการสินค้าไม่สำเร็จ กรุณากดบันทึกอีกครั้ง";
const ITEMS_LOCKED =
  "สมุดราคานี้ถูกสงวนไว้โดยระบบ จึงแก้ไขรายการสินค้าไม่ได้";

export interface SavePricebookPricesInput {
  pricebookUuid: string;
  /** The new book %, or null when it is unchanged. */
  bookDiscountPercent: number | null;
  items: PricebookItemInput[];
}

export interface SavePricebookPricesOutcome {
  /** True once the new book % is stored, even if the lines then failed. */
  isBookDiscountSaved: boolean;
  /** Thai reason the save stopped short; empty when everything landed. */
  errorMessage: string;
}

const itemsFailureMessage = (
  error: unknown,
  isBookDiscountSaved: boolean,
): string => {
  if (isAxiosError(error) && error.response?.status === HTTP_CONFLICT) {
    return ITEMS_LOCKED;
  }
  return isBookDiscountSaved ? ITEMS_FAILED_AFTER_BOOK_DISCOUNT : ITEMS_FAILED;
};

/**
 * Writes the book % first (the API re-prices every line without its own %), then the whole
 * line set, which the API prices again at the stored %. Every line is sent even when only the
 * book % changed: the confirmation priced them all, and lines not yet moved to the discount
 * model only get their unit price from this write. Announced once, when both have landed.
 */
export const savePricebookPrices = async ({
  pricebookUuid,
  bookDiscountPercent,
  items,
}: SavePricebookPricesInput): Promise<SavePricebookPricesOutcome> => {
  let isBookDiscountSaved = false;
  if (bookDiscountPercent !== null) {
    try {
      await updatePriceBook(pricebookUuid, {
        discount_percent: bookDiscountPercent,
      });
      isBookDiscountSaved = true;
    } catch {
      // The axios interceptor already showed the API's own wording.
      return { isBookDiscountSaved, errorMessage: BOOK_DISCOUNT_FAILED };
    }
  }

  try {
    await replacePricebookItems(pricebookUuid, items);
  } catch (error) {
    return {
      isBookDiscountSaved,
      errorMessage: itemsFailureMessage(error, isBookDiscountSaved),
    };
  }

  pushGlobalSuccess(PRICES_SAVED_NOTICE);
  return { isBookDiscountSaved, errorMessage: "" };
};
